import Phaser from 'phaser'

import { Hud } from '../../game/ui/Hud'
import { ACTIVE_CAMPAIGN_ID } from '../config/campaign'
import {
  getCampaignDictionary,
  getCampaignEventTokenTextureKey,
  getCampaignEvents,
  getCampaignMapData,
  getCampaignTranslation,
  getCampaignTileTextureKey,
} from '../config/campaignAssets'
import { CampaignEventManager } from '../managers/CampaignEventManager'
import type { CampaignEventContext } from '../managers/CampaignEventManager'
import type { CampaignEvents } from '../config/campaignAssets'

type MapTileInstance = {
  image: Phaser.GameObjects.Image
  baseKey: string
  minRow: number
  maxRow: number
  minCol: number
  maxCol: number
  pixelWidth: number
  pixelHeight: number
  tileId?: number
}

export class GameScene extends Phaser.Scene {
  private hud?: Hud

  private playArea?: Phaser.GameObjects.Rectangle

  private pointerIndicator?: Phaser.GameObjects.Arc

  private inputState = {
    dragging: false,
    dragStart: new Phaser.Math.Vector2(),
    lastPointer: new Phaser.Math.Vector2(),
  }

  private getTileInstanceById(tileId: number): MapTileInstance | undefined {
    return this.mapTiles.find((tile) => tile.tileId === tileId)
  }

  private getTileInstanceAt(row: number, col: number): MapTileInstance | undefined {
    return this.mapTiles.find((tile) => row >= tile.minRow && row <= tile.maxRow && col >= tile.minCol && col <= tile.maxCol)
  }

  private handleTap(worldX: number, worldY: number): void {
    if (!this.mapContainer) {
      return
    }

    if (this.suppressNextTap) {
      this.suppressNextTap = false
      return
    }

    if (this.dialogModal) {
      this.hideDialogModal()
      return
    }

    this.stopModalAudio()

    const events = Object.values(this.campaignEvents).flat()

    if (events.length === 0) {
      return
    }

    const activeEvent = this.eventManager.findEventAt(worldX, worldY, events)

    if (!activeEvent || !this.eventManager.isTriggerSatisfied(activeEvent.trigger)) {
      return
    }

    this.eventManager.executeActions(activeEvent)
  }
  private createEventContext(): CampaignEventContext {
    return {
      scene: this,
      getMapContainer: () => this.mapContainer,
      getEventHitArea: (eventId) => this.eventHitAreas[eventId],
      getEventObject: (eventId) => this.eventObjectsById[eventId],
      getTileLocation: (eventId) => this.tileEvents[eventId],
      getTileInstanceAt: (row, col) => this.getTileInstanceAt(row, col),
      isEventTileVisible: (eventId) => {
        const baseKey = eventId.replace(/[^A-Za-z]/g, '')
        return this.tileVisibility[baseKey] !== false
      },
      getCellMetrics: () => ({
        mapRows: this.mapRows,
        mapCols: this.mapCols,
        maxCellWidthPx: this.maxCellWidthPx,
        maxCellHeightPx: this.maxCellHeightPx,
      }),
      setHint: (hint) => {
        this.hud?.setHint(hint)
      },
      stopModalAudio: () => {
        this.stopModalAudio()
      },
      showDialogModal: (textKey, subtitle) => {
        this.showDialogModal(textKey, subtitle)
      },
      revealTile: (tileId) => {
        this.setTileVisibilityById(tileId, true)
      },
      openDoor: (tileId) => {
        this.setTileVisibilityById(tileId, true)
      },
      playVoice: (audioKey) => {
        this.playVoiceAudio(audioKey)
      },
      playSfx: (audioKey) => {
        this.playSfxAudio(audioKey)
      },
      isGoalCompleted: () => this.goalCompleted,
      markGoalCompleted: () => {
        this.goalCompleted = true
      },
      endGoal: (reasonKey) => {
        this.completeGoal(reasonKey)
      },
      getFlag: (key) => this.campaignFlags[key] === true,
      setFlag: (key, value) => {
        this.campaignFlags[key] = value
      },
    }
  }

  private playVoiceAudio(audioKey: string): void {
    this.stopModalAudio()

    const existing = this.sound.get(audioKey)

    if (existing) {
      existing.stop()
      existing.play()
      this.activeModalSoundKey = audioKey
      return
    }

    try {
      const soundInstance = this.sound.add(audioKey)
      soundInstance.play()
      this.activeModalSoundKey = audioKey
    } catch (error) {
      console.log(`No se pudo reproducir la voz "${audioKey}":`, error)
    }
  }

  private playSfxAudio(audioKey: string): void {
    this.stopModalAudio()

    const existing = this.sound.get(audioKey)

    if (existing) {
      existing.play()
      this.activeModalSoundKey = audioKey
      return
    }

    try {
      const soundInstance = this.sound.add(audioKey)
      soundInstance.play()
      this.activeModalSoundKey = audioKey
    } catch (error) {
      console.log(`No se pudo reproducir el efecto "${audioKey}":`, error)
    }
  }

  private completeGoal(reasonKey: string): void {
    this.showDialogModal(reasonKey)
    this.time.delayedCall(2800, () => {
      this.stopModalAudio()
      this.sound.stopAll()
      this.scene.stop('GameScene')
      this.scene.start('MenuScene')
    })
  }

  private setTileVisibilityById(tileId: number, visible: boolean): void {
    const tileInstance = this.getTileInstanceById(tileId)

    if (!tileInstance) {
      console.log(`No se encontró la loseta con id ${tileId}`)
      return
    }

    tileInstance.image.setVisible(visible)
    this.tileVisibility[tileInstance.baseKey] = visible

    if (visible) {
      this.discoveredTileIds.add(tileId)
    } else {
      this.discoveredTileIds.delete(tileId)
    }

    this.layoutMapTiles(
      this.scale.width,
      this.playArea?.y ?? this.scale.height / 2,
      this.playArea?.width ?? 0,
      this.playArea?.height ?? 0
    )
    this.applyMapMask()
  }

  private centerMapOnPoint(localPosition: Phaser.Math.Vector2, targetWorldX: number, targetWorldY: number): void {
    if (!this.mapContainer) {
      return
    }

    const scale = this.baseMapScale * this.currentZoom
    if (scale <= 0) {
      return
    }

    const containerX = targetWorldX - localPosition.x * scale
    const containerY = targetWorldY - localPosition.y * scale

    this.mapContainer.setPosition(containerX, containerY)
  }

  private confirmationModal?: Phaser.GameObjects.Container
  private savePromptModal?: Phaser.GameObjects.Container
  private mapContainer?: Phaser.GameObjects.Container
  private mapMask?: Phaser.Display.Masks.GeometryMask
  private mapMaskShape?: Phaser.GameObjects.Rectangle
  private mapMaskOutline?: Phaser.GameObjects.Rectangle
  private mapTiles: MapTileInstance[] = []
  private mapRows = 0
  private mapCols = 0
  private tileEvents: Record<string, { row: number; col: number }> = {}
  private maxCellWidthPx = 0
  private maxCellHeightPx = 0
  private baseMapScale = 1
  private currentZoom = 1
  private readonly minZoom = 1.25
  private readonly maxZoom = 1.7
  private readonly zoomStep = 0.15
  private defaultZoom = 1.55
  private readonly clampPadding = { x: 160, y: 220 }
  private readonly clampYOffset = 90
  private pinchRecognizer?: {
    isPinching: boolean
    initialDistance: number
    initialZoom: number
    centerLocalX: number
    centerLocalY: number
  }
  private activePointers = new Map<number, Phaser.Input.Pointer>()
  private clampBounds = { width: 0, height: 0, centerX: 0, centerY: 0 }
  private campaignEvents: CampaignEvents = {}
  private eventMarkers: Phaser.GameObjects.GameObject[] = []
  private eventSprites: Phaser.GameObjects.Image[] = []
  private eventObjectsById: Record<string, Phaser.GameObjects.Image | Phaser.GameObjects.Text> = {}
  private tileVisibility: Record<string, boolean> = {}
  private tileCentersById: Record<number, Phaser.Math.Vector2> = {}
  private focusTileId?: number
  private discoveredTileIds = new Set<number>()
  private dialogModal?: Phaser.GameObjects.Container
  private readonly campaignLocale: string
  private campaignFlags: Record<string, boolean> = {}
  private goalCompleted = false
  private activeModalSoundKey?: string
  private eventHitAreas: Record<string, { x: number; y: number; halfWidth: number; halfHeight: number }> = {}
  private readonly eventManager: CampaignEventManager
  private suppressNextTap = false

  constructor() {
    super('GameScene')
    const language = typeof navigator === 'undefined' ? 'es' : navigator.language
    const normalized = language.split('-')[0] || 'es'
    this.campaignLocale = normalized.toLowerCase()
    this.eventManager = new CampaignEventManager(this.createEventContext())
  }

  create(): void {
    const extraPointers = Math.max(0, 2 - (this.input.manager.pointersTotal - 1))
    for (let index = 0; index < extraPointers; index += 1) {
      this.input.addPointer(1)
    }

    this.playArea = this.add
      .rectangle(0, 0, 100, 100, 0x1f2937, 0)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(0, 0, 0)
      .setDepth(1)

    this.hud = new Hud(this, {
      objective: 'Encuentra el dispositivo antes de que el enemigo lo active.',
      hint: 'Explora la jungla y derrota al enemigo para conseguir pistas.',
    })

    this.events.on('hud-menu', this.handleHudMenu, this)

    this.pointerIndicator = this.add
      .circle(0, 0, 18, 0xf7b538, 0.18)
      .setStrokeStyle(2, 0xf7b538, 0.85)
      .setDepth(3)
      .setVisible(false)

    this.mapContainer = this.add.container(0, 0)
    this.mapContainer.setDepth(2)

    this.loadCampaignMap()
    this.scale.on('resize', this.handleResize, this)
    this.input.on('wheel', this.handlePointerWheel, this)

    this.events.on('hud-menu', this.handleHudMenu, this)

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.handleResize, this)
      this.input.off('wheel', this.handlePointerWheel, this)
      this.events.off('hud-menu', this.handleHudMenu, this)
      this.hud?.destroy()
      this.destroyMap()
    })

    this.handleResize()
    this.registerInputHandlers()

    this.ensureCampaignTheme()
    this.time.delayedCall(4200, () => {
      this.hud?.setHint('Revisa la jungla, quizás encuentres alguna pista.')
    })
  }

  private handleResize(gameSize?: Phaser.Structs.Size): void {
    const width = gameSize?.width ?? this.scale.width
    const height = gameSize?.height ?? this.scale.height

    this.hud?.resize(width, height)

    const topInset = this.hud?.getTopHeight() ?? 0
    const bottomInset = this.hud?.getBottomHeight() ?? 0
    const safePadding = Math.max(16, Math.round(Math.min(width, height) * 0.02))

    const availableHeight = Math.max(200, height - topInset - bottomInset - safePadding * 2)
    const availableWidth = Math.max(320, width - safePadding * 2)
    const playAreaY = topInset + safePadding + availableHeight / 2

    this.playArea?.setSize(availableWidth, availableHeight)
    this.playArea?.setPosition(width / 2, playAreaY)

    const clampWidth = Math.max(160, availableWidth - this.clampPadding.x)
    const clampHeight = Math.max(200, availableHeight - this.clampPadding.y)

    this.clampBounds = {
      width: clampWidth,
      height: clampHeight,
      centerX: width / 2,
      centerY: playAreaY + this.clampYOffset,
    }

    this.positionModal(this.confirmationModal, width, height)
    this.positionModal(this.savePromptModal, width, height)
    this.positionModal(this.dialogModal, width, height)

    this.layoutMapTiles(width, playAreaY, availableWidth, availableHeight)
    this.applyMapMask()

    this.currentZoom = Phaser.Math.Clamp(this.defaultZoom, this.minZoom, this.maxZoom)
    this.updateMapScale()
  }

  private registerInputHandlers(): void {
    this.input.on('pointerdown', this.onPointerDown, this)
    this.input.on('pointermove', this.onPointerMove, this)
    this.input.on('pointerup', this.onPointerUp, this)
    this.input.on('pointerupoutside', this.onPointerUp, this)
    this.input.on('wheel', this.handlePointerWheel, this)

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off('pointerdown', this.onPointerDown, this)
      this.input.off('pointermove', this.onPointerMove, this)
      this.input.off('pointerup', this.onPointerUp, this)
      this.input.off('pointerupoutside', this.onPointerUp, this)
      this.input.off('wheel', this.handlePointerWheel, this)
    })
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    this.pendingTap = { x: pointer.worldX, y: pointer.worldY }
    this.activePointers.set(pointer.id, pointer)
    const activePointers = this.getActivePointers()

    if (activePointers.length >= 2) {
      this.handlePinchStart(activePointers[0], activePointers[1])
      this.inputState.dragging = false
      this.hidePointerIndicator()
      return
    }

    this.pinchRecognizer = undefined
    this.inputState.dragging = false
    this.inputState.dragStart.set(pointer.worldX, pointer.worldY)
    this.inputState.lastPointer.set(pointer.worldX, pointer.worldY)
    this.showPointerIndicator(pointer)
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    this.activePointers.set(pointer.id, pointer)
    const activePointers = this.getActivePointers()

    if (activePointers.length >= 2) {
      if (!this.pinchRecognizer?.isPinching) {
        this.handlePinchStart(activePointers[0], activePointers[1])
      } else {
        this.handlePinch(activePointers[0], activePointers[1])
      }
      return
    }

    if (this.pinchRecognizer?.isPinching) {
      this.handlePinchEnd()
    }

    if (!pointer.isDown) {
      this.inputState.dragging = false
      this.hidePointerIndicator()
      return
    }

    if (!this.inputState.dragging) {
      const dragDistance = Phaser.Math.Distance.Between(
        this.inputState.dragStart.x,
        this.inputState.dragStart.y,
        pointer.worldX,
        pointer.worldY
      )

      if (dragDistance >= 6) {
        this.inputState.dragging = true
      }
    }

    if (this.inputState.dragging && this.playArea) {
      const deltaX = pointer.worldX - this.inputState.lastPointer.x
      const deltaY = pointer.worldY - this.inputState.lastPointer.y

      this.playArea.x += deltaX
      this.playArea.y += deltaY

      if (this.mapContainer) {
        this.mapContainer.x += deltaX
        this.mapContainer.y += deltaY
        this.clampMapPosition()
      }

      this.inputState.lastPointer.set(pointer.worldX, pointer.worldY)
    }

    this.showPointerIndicator(pointer)
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    const wasDragging = this.inputState.dragging
    this.inputState.dragging = false
    this.hidePointerIndicator()

    const tappedPosition = this.pendingTap
    this.pendingTap = undefined

    this.activePointers.delete(pointer.id)

    if (this.pinchRecognizer?.isPinching) {
      const activePointers = this.getActivePointers()
      if (activePointers.length < 2) {
        this.handlePinchEnd()
      }
    }

    if (!wasDragging && this.mapContainer) {
      const bounds = this.mapContainer.getBounds()
      const tapX = tappedPosition?.x ?? pointer.worldX
      const tapY = tappedPosition?.y ?? pointer.worldY

      if (bounds.contains(tapX, tapY)) {
        this.showClickFeedback(tapX, tapY)
        this.handleTap(tapX, tapY)
      }
    }
  }

  private pendingTap?: { x: number; y: number }

  private showPointerIndicator(pointer: Phaser.Input.Pointer): void {
    if (!this.pointerIndicator) {
      return
    }

    this.pointerIndicator.setVisible(true)
    this.pointerIndicator.setPosition(pointer.worldX, pointer.worldY)
    this.pointerIndicator.setScale(this.inputState.dragging ? 1.1 : 1)
  }

  private hidePointerIndicator(): void {
    this.pointerIndicator?.setVisible(false)
  }

  private showClickFeedback(x: number, y: number): void {
    const ripple = this.add.circle(x, y, 0, 0xf7b538, 0.45).setDepth(Number.MAX_SAFE_INTEGER - 2)

    this.tweens.add({
      targets: ripple,
      radius: 36,
      alpha: 0,
      duration: 400,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        ripple.destroy()
      },
    })
  }

  private ensureCampaignTheme(): void {
    if (!this.sound || !this.cache.audio.exists('campaign-theme')) {
      return
    }

    const theme = this.sound.get('campaign-theme') ?? this.sound.add('campaign-theme', { loop: true, volume: 0.3 })

    if (!theme.isPlaying) {
      theme.play()
    }
  }

  private positionModal(modal: Phaser.GameObjects.Container | undefined, width: number, height: number): void {
    if (!modal) {
      return
    }

    modal.setPosition(width / 2, height / 2)
  }

  private handleHudMenu(): void {
    this.pauseGameAudio()
    this.showConfirmationModal()
  }

  private createModal(title: string, body: string, confirmLabel: string, cancelLabel: string, onConfirm: () => void, onCancel: () => void): Phaser.GameObjects.Container {
    const modalWidth = Math.min(this.scale.width * 0.85, 540)
    const modalHeight = Math.min(this.scale.height * 0.82, 380)

    const background = this.add
      .rectangle(0, 0, modalWidth, modalHeight, 0x0f172a, 0.94)
      .setStrokeStyle(2, 0xf7b538, 0.8)
      .setOrigin(0.5, 0.5)

    const titleText = this.add
      .text(0, -modalHeight / 2 + 48, title, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '26px',
        fontStyle: 'bold',
        color: '#f9fafb',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)

    const bodyText = this.add
      .text(0, 0, body, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '19px',
        color: '#e5e7eb',
        align: 'center',
        wordWrap: { width: modalWidth - 64 },
        lineSpacing: 6,
      })
      .setOrigin(0.5, 0)

    const titleBottom = titleText.y + titleText.height / 2
    let bodyTop = titleBottom + 18
    bodyText.setPosition(0, bodyTop)

    const maxBodyHeight = modalHeight - 190
    let fontSize = 19

    while (bodyText.height > maxBodyHeight && fontSize > 14) {
      fontSize -= 1
      bodyText.setFontSize(fontSize)
    }

    if (bodyText.height > maxBodyHeight) {
      bodyText.setWordWrapWidth(modalWidth - 72)
    }

    const minBodyTop = titleBottom + 16
    bodyTop = Math.max(bodyTop, minBodyTop)
    bodyText.setY(bodyTop)

    const bodyDisplayHeight = bodyText.displayHeight
    const desiredPadding = 32
    let buttonY = modalHeight / 2 - 36
    let buttonsTop = bodyTop + bodyDisplayHeight + desiredPadding

    if (buttonsTop > buttonY) {
      const overflow = buttonsTop - buttonY
      bodyTop = Math.max(minBodyTop, bodyTop - overflow)
      bodyText.setY(bodyTop)
      buttonsTop = bodyTop + bodyDisplayHeight + desiredPadding
      buttonY = Math.min(modalHeight / 2 - 24, buttonsTop)
    }

    buttonY = Math.min(buttonY, modalHeight / 2 - 24)
    const buttonOffsetX = Math.min(modalWidth / 3, 110)

    const confirmButton = this.createModalButton(confirmLabel, -buttonOffsetX, buttonY, onConfirm)
    const cancelButton = this.createModalButton(cancelLabel, buttonOffsetX, buttonY, onCancel)

    const container = this.add.container(this.scale.width / 2, this.scale.height / 2, [background, titleText, bodyText, confirmButton, cancelButton])
    container.setDepth(Number.MAX_SAFE_INTEGER)

    return container
  }

  private createModalButton(label: string, offsetX: number, y: number, handler: () => void): Phaser.GameObjects.Text {
    const button = this.add
      .text(offsetX, y, label, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '19px',
        color: '#f7b538',
        backgroundColor: 'rgba(30, 41, 59, 0.85)',
        padding: { x: 18, y: 10 },
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })

    button.on('pointerdown', handler)

    return button
  }

  private showConfirmationModal(): void {
    this.destroyModal(this.confirmationModal)
    this.destroyModal(this.savePromptModal)

    this.confirmationModal = this.createModal(
      '¿Volver al menú principal?',
      'Perderás el progreso no guardado si sales ahora.',
      'Sí, salir',
      'Cancelar',
      () => {
        this.destroyModal(this.confirmationModal)
        this.showSavePromptModal()
      },
      () => {
        this.destroyModal(this.confirmationModal)
        this.resumeGameAudio()
      }
    )
  }

  private showSavePromptModal(): void {
    this.destroyModal(this.savePromptModal)

    this.savePromptModal = this.createModal(
      '¿Deseas guardar la partida?',
      'Puedes continuar más tarde si guardas ahora.',
      'Guardar y salir',
      'Salir sin guardar',
      () => {
        this.handleSaveAndExit()
      },
      () => {
        this.handleExitWithoutSave()
      }
    )
  }

  private destroyModal(modal?: Phaser.GameObjects.Container): void {
    modal?.destroy(true)
  }

  private showDialogModal(textKey: string, subtitle?: string): void {
    this.hideDialogModal()

    const message = this.resolveCampaignText(textKey)
    const width = Math.min(this.scale.width * 0.7, 520)
    const height = Math.min(this.scale.height * 0.5, 280)

    const background = this.add
      .rectangle(0, 0, width, height, 0x0f172a, 0.92)
      .setStrokeStyle(2, 0xf7b538, 0.85)
      .setOrigin(0.5, 0.5)

    const messageText = this.add
      .text(0, -height * 0.24, message, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '22px',
        color: '#e5e7eb',
        align: 'center',
        wordWrap: { width: width - 64 },
        lineSpacing: 6,
      })
      .setOrigin(0.5, 0)

    let subtitleText: Phaser.GameObjects.Text | undefined

    if (typeof subtitle === 'string' && subtitle.trim().length > 0) {
      subtitleText = this.add
        .text(0, messageText.y + messageText.displayHeight + 16, subtitle, {
          fontFamily: 'Segoe UI, system-ui, sans-serif',
          fontSize: '18px',
          color: '#cbd5f5',
          align: 'center',
          wordWrap: { width: width - 72 },
        })
        .setOrigin(0.5, 0)
    }

    const buttonLabel = this.resolveCampaignText('ui.ok')
    const button = this.add
      .text(0, height * 0.28, buttonLabel, {
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        fontSize: '20px',
        color: '#f7b538',
        backgroundColor: 'rgba(30, 41, 59, 0.85)',
        padding: { x: 22, y: 10 },
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })

    const children: Phaser.GameObjects.GameObject[] = [background, messageText]

    if (subtitleText) {
      children.push(subtitleText)
    }

    children.push(button)

    const container = this.add.container(this.scale.width / 2, this.scale.height / 2, children)
    container.setDepth(Number.MAX_SAFE_INTEGER - 1)

    button.on('pointerdown', () => {
      this.suppressNextTap = true
      this.hideDialogModal()
    })

    this.dialogModal = container
    this.dialogModal.setName('dialog-modal')
  }

  private hideDialogModal(): void {
    if (!this.dialogModal) {
      return
    }

    this.dialogModal.destroy(true)
    this.dialogModal = undefined
    this.stopModalAudio()
  }

  private handleSaveAndExit(): void {
    // TODO: Integrar lógica real de guardado cuando esté disponible.
    this.destroyModal(this.savePromptModal)
    this.sound.get('campaign-theme')?.stop()
    this.scene.stop('GameScene')
    this.scene.start('MenuScene')
  }

  private handleExitWithoutSave(): void {
    this.destroyModal(this.savePromptModal)
    this.sound.get('campaign-theme')?.stop()
    this.scene.stop('GameScene')
    this.scene.start('MenuScene')
  }

  private pauseGameAudio(): void {
    this.sound.pauseAll()
  }

  private resumeGameAudio(): void {
    this.sound.resumeAll()
  }

  private loadCampaignMap(): void {
    const dictionary = getCampaignDictionary(ACTIVE_CAMPAIGN_ID)
    const mapData = getCampaignMapData(ACTIVE_CAMPAIGN_ID)
    const events = getCampaignEvents(ACTIVE_CAMPAIGN_ID)

    if (!dictionary || !mapData) {
      console.log('No se pudo cargar el mapa o el diccionario de la campaña.')
      return
    }

    const spawnTileId = Object.values(dictionary.tiles).find((tile) => tile.spawn)?.id
    const firstVisibleTileId = Object.values(dictionary.tiles).find((tile) => tile.show !== false)?.id
    this.focusTileId = spawnTileId ?? firstVisibleTileId

    this.campaignEvents = events ?? {}

    this.eventMarkers.forEach((marker) => marker.destroy())
    this.eventMarkers = []
    this.eventSprites.forEach((sprite) => sprite.destroy())
    this.eventSprites = []
    Object.values(this.eventObjectsById).forEach((object) => object.destroy())
    this.eventObjectsById = {}
    this.campaignFlags = {}
    this.goalCompleted = false
    this.activeModalSoundKey = undefined

    const rows = mapData
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.split(';'))

    this.mapRows = rows.length
    this.mapCols = rows.reduce((max, row) => Math.max(max, row.length), 0)

    if (!this.mapContainer) {
      this.mapContainer = this.add.container(0, 0)
    }

    this.mapTiles.forEach((tile) => tile.image.destroy())
    this.mapTiles = []
    this.tileEvents = {}
    this.maxCellWidthPx = 0
    this.maxCellHeightPx = 0
    this.baseMapScale = 1
    this.tileVisibility = {}
    this.discoveredTileIds.clear()

    const tileKeys = Object.keys(dictionary.tiles).sort((a, b) => b.length - a.length)
    const grouped = new Map<string, MapTileInstance>()

    rows.forEach((row, rowIndex) => {
      row.forEach((token, colIndex) => {
        const trimmed = token.trim()

        if (!trimmed) {
          return
        }

        const segments: string[] = []
        let cursor = 0

        while (cursor < trimmed.length) {
          let matched = false

          for (const key of tileKeys) {
            if (trimmed.startsWith(key, cursor)) {
              let end = cursor + key.length
              while (end < trimmed.length && /[0-9]/.test(trimmed[end])) {
                end += 1
              }
              segments.push(trimmed.slice(cursor, end))
              cursor = end
              matched = true
              break
            }
          }

          if (!matched) {
            const char = trimmed[cursor]
            if (/[A-Za-z]/.test(char)) {
              let end = cursor + 1
              while (end < trimmed.length && /[0-9]/.test(trimmed[end])) {
                end += 1
              }
              segments.push(trimmed.slice(cursor, end))
              cursor = end
            } else {
              cursor += 1
            }
          }
        }

        segments.forEach((segment) => {
          const baseKey = segment.replace(/[^A-Za-z]/g, '')
          const tileDef = dictionary.tiles[baseKey]

          if (!tileDef) {
            console.log(`Tile desconocido en mapa: "${segment}" derivado de "${trimmed}"`)
            return
          }

          const spriteCandidates = tileDef.sprite ? [tileDef.sprite, baseKey] : [baseKey]
          const textureKey = spriteCandidates
            .map((candidate) => getCampaignTileTextureKey(ACTIVE_CAMPAIGN_ID, candidate))
            .find((candidateKey) => this.textures.exists(candidateKey))

          if (!textureKey) {
            console.log(
              `Textura no encontrada para tile "${segment}" (sprite esperado: "${tileDef.sprite}" en "${trimmed}")`
            )
            return
          }

          if (/\d/.test(segment)) {
            this.tileEvents[segment] = { row: rowIndex, col: colIndex }
          }

          const key = `${baseKey}`
          const existing = grouped.get(key)

          if (existing) {
            existing.minRow = Math.min(existing.minRow, rowIndex)
            existing.maxRow = Math.max(existing.maxRow, rowIndex)
            existing.minCol = Math.min(existing.minCol, colIndex)
            existing.maxCol = Math.max(existing.maxCol, colIndex)
          } else {
            const image = this.add.image(0, 0, textureKey)
            image.setOrigin(0.5, 0.5)
            image.setDepth(2)

            const frame = this.textures.get(textureKey).get()
            const pixelWidth = frame?.width ?? image.width
            const pixelHeight = frame?.height ?? image.height
            const isVisible = tileDef.show !== false
            image.setVisible(isVisible)

            const instance: MapTileInstance = {
              image,
              baseKey,
              minRow: rowIndex,
              maxRow: rowIndex,
              minCol: colIndex,
              maxCol: colIndex,
              pixelWidth,
              pixelHeight,
              tileId: tileDef.id,
            }

            grouped.set(key, instance)
            this.mapContainer?.add(image)
          }

          const isVisible = tileDef.show !== false
          this.tileVisibility[baseKey] = isVisible

          if (isVisible && typeof tileDef.id === 'number') {
            this.discoveredTileIds.add(tileDef.id)
          }
        })
      })
    })

    this.mapTiles = Array.from(grouped.values())

    this.mapTiles.forEach((tile) => {
      const tileCols = tile.maxCol - tile.minCol + 1
      const tileRows = tile.maxRow - tile.minRow + 1
      const cellWidthPx = tile.pixelWidth / tileCols
      const cellHeightPx = tile.pixelHeight / tileRows
      this.maxCellWidthPx = Math.max(this.maxCellWidthPx, cellWidthPx)
      this.maxCellHeightPx = Math.max(this.maxCellHeightPx, cellHeightPx)
    })
  }

  private layoutMapTiles(width: number, playAreaCenterY: number, areaWidth: number, areaHeight: number): void {
    if (!this.mapContainer || this.mapTiles.length === 0 || areaWidth === 0 || areaHeight === 0) {
      return
    }

    const cellWidthPx = this.maxCellWidthPx || 1
    const cellHeightPx = this.maxCellHeightPx || 1

    const rows = Math.max(1, this.mapRows)
    const cols = Math.max(1, this.mapCols)

    const mapWidthPx = cols * cellWidthPx
    const mapHeightPx = rows * cellHeightPx

    const scale = Math.min(areaWidth / mapWidthPx, areaHeight / mapHeightPx)

    const halfWidthPx = mapWidthPx / 2
    const halfHeightPx = mapHeightPx / 2

    this.tileCentersById = {}
    let visibleWeight = 0
    let visibleSumX = 0
    let visibleSumY = 0

    this.mapTiles.forEach((tile) => {
      const tileCols = tile.maxCol - tile.minCol + 1
      const tileRows = tile.maxRow - tile.minRow + 1
      tile.image.setScale(1)
      tile.image.setDisplaySize(tile.pixelWidth, tile.pixelHeight)

      const offsetX = tile.minCol * cellWidthPx + tile.pixelWidth / 2
      const offsetY = tile.minRow * cellHeightPx + tile.pixelHeight / 2

      const positionX = -halfWidthPx + offsetX
      const positionY = -halfHeightPx + offsetY

      tile.image.setPosition(positionX, positionY)

      if (typeof tile.tileId === 'number') {
        this.tileCentersById[tile.tileId] = new Phaser.Math.Vector2(positionX, positionY)
      }

      if (tile.image.visible) {
        const tileArea = Math.max(1, tileCols * tileRows)
        visibleSumX += positionX * tileArea
        visibleSumY += positionY * tileArea
        visibleWeight += tileArea
      }
    })

    this.baseMapScale = scale
    this.updateMapScale()
    this.mapContainer.setPosition(width / 2, playAreaCenterY)

    if (visibleWeight > 0) {
      const centroid = new Phaser.Math.Vector2(visibleSumX / visibleWeight, visibleSumY / visibleWeight)
      this.centerMapOnPoint(centroid, width / 2, playAreaCenterY)
    } else if (typeof this.focusTileId === 'number') {
      this.centerMapOnTile(this.focusTileId, width / 2, playAreaCenterY)
    }

    this.updateEventMarkers(cellWidthPx, cellHeightPx, halfWidthPx, halfHeightPx)
    this.clampMapPosition()
    this.applyMapMask()
  }

  private destroyMap(): void {
    this.mapTiles.forEach((tile) => tile.image.destroy())
    this.mapTiles = []
    this.tileEvents = {}
    this.maxCellWidthPx = 0
    this.maxCellHeightPx = 0
    this.baseMapScale = 1
    this.currentZoom = 1
    this.defaultZoom = 1.55
    this.activePointers.clear()
    this.clampBounds = { width: 0, height: 0, centerX: 0, centerY: 0 }
    this.campaignEvents = {}
    this.tileVisibility = {}
    this.eventMarkers.forEach((marker) => marker.destroy())
    this.eventMarkers = []
    this.eventSprites.forEach((sprite) => sprite.destroy())
    this.eventSprites = []
    Object.values(this.eventObjectsById).forEach((object) => object.destroy())
    this.eventObjectsById = {}
    this.tileCentersById = {}
    this.eventHitAreas = {}
    this.focusTileId = undefined
    this.discoveredTileIds.clear()
    this.campaignFlags = {}
    this.goalCompleted = false
    this.activeModalSoundKey = undefined
    this.hideDialogModal()
    this.mapContainer?.destroy(true)
    this.mapContainer = undefined
    this.mapMask?.destroy()
    this.mapMask = undefined
    this.mapMaskShape?.destroy()
    this.mapMaskShape = undefined
    this.mapMaskOutline?.destroy()
    this.mapMaskOutline = undefined
  }

  private updateMapScale(): void {
    if (!this.mapContainer) {
      return
    }

    const appliedScale = this.baseMapScale * this.currentZoom
    this.mapContainer.setScale(appliedScale)
  }

  private applyMapMask(): void {
    if (!this.mapContainer || !this.playArea) {
      return
    }

    this.mapContainer.clearMask()
    this.mapMask?.destroy()
    this.mapMask = undefined
    this.mapMaskShape?.destroy()
    this.mapMaskShape = undefined

    if (this.mapMaskOutline) {
      this.mapMaskOutline.setVisible(false)
    }
  }

  private handlePointerWheel(
    pointer: Phaser.Input.Pointer,
    _gameObjects: Phaser.GameObjects.GameObject[],
    _deltaX: number,
    deltaY: number
  ): void {
    if (!this.mapContainer || !this.playArea || this.baseMapScale <= 0) {
      return
    }

    const bounds = this.playArea.getBounds()
    if (!bounds.contains(pointer.worldX, pointer.worldY)) {
      return
    }

    const zoomDelta = deltaY > 0 ? -this.zoomStep : this.zoomStep
    const previousZoom = this.currentZoom
    const newZoom = Phaser.Math.Clamp(previousZoom + zoomDelta, this.minZoom, this.maxZoom)

    if (Math.abs(newZoom - previousZoom) < 0.0001) {
      return
    }

    const previousScale = this.baseMapScale * previousZoom
    if (previousScale <= 0) {
      return
    }

    const pointerWorldX = pointer.worldX
    const pointerWorldY = pointer.worldY
    const offsetX = pointerWorldX - this.mapContainer.x
    const offsetY = pointerWorldY - this.mapContainer.y
    const localX = offsetX / previousScale
    const localY = offsetY / previousScale

    this.currentZoom = newZoom
    this.updateMapScale()

    const newScale = this.baseMapScale * this.currentZoom
    const newPosX = pointerWorldX - localX * newScale
    const newPosY = pointerWorldY - localY * newScale
    this.mapContainer.setPosition(newPosX, newPosY)
    this.clampMapPosition()
  }

  private handlePinchStart(pointerA: Phaser.Input.Pointer, pointerB: Phaser.Input.Pointer): void {
    if (!this.mapContainer || !this.playArea || this.baseMapScale <= 0) {
      return
    }

    const distance = Phaser.Math.Distance.Between(pointerA.worldX, pointerA.worldY, pointerB.worldX, pointerB.worldY)

    if (distance <= 0) {
      return
    }

    const currentScale = this.baseMapScale * this.currentZoom

    if (currentScale <= 0) {
      return
    }

    const centerWorldX = (pointerA.worldX + pointerB.worldX) / 2
    const centerWorldY = (pointerA.worldY + pointerB.worldY) / 2
    const centerLocalX = (centerWorldX - this.mapContainer.x) / currentScale
    const centerLocalY = (centerWorldY - this.mapContainer.y) / currentScale

    this.pinchRecognizer = {
      isPinching: true,
      initialDistance: distance,
      initialZoom: this.currentZoom,
      centerLocalX,
      centerLocalY,
    }
  }

  private handlePinch(
    pointerA: Phaser.Input.Pointer,
    pointerB: Phaser.Input.Pointer
  ): void {
    if (!this.pinchRecognizer || !this.pinchRecognizer.isPinching || !this.mapContainer) {
      return
    }

    const distance = Phaser.Math.Distance.Between(pointerA.worldX, pointerA.worldY, pointerB.worldX, pointerB.worldY)

    if (distance <= 0 || this.pinchRecognizer.initialDistance <= 0) {
      return
    }

    const scaleRatio = distance / this.pinchRecognizer.initialDistance
    const targetZoom = Phaser.Math.Clamp(this.pinchRecognizer.initialZoom * scaleRatio, this.minZoom, this.maxZoom)

    if (Math.abs(targetZoom - this.currentZoom) < 0.0001) {
      return
    }

    const previousScale = this.baseMapScale * this.currentZoom

    this.currentZoom = targetZoom
    this.updateMapScale()

    const newScale = this.baseMapScale * this.currentZoom
    if (previousScale <= 0 || newScale <= 0) {
      return
    }

    const centerWorldX = (pointerA.worldX + pointerB.worldX) / 2
    const centerWorldY = (pointerA.worldY + pointerB.worldY) / 2
    const newPosX = centerWorldX - this.pinchRecognizer.centerLocalX * newScale
    const newPosY = centerWorldY - this.pinchRecognizer.centerLocalY * newScale
    this.mapContainer.setPosition(newPosX, newPosY)
    this.clampMapPosition()
  }

  private handlePinchEnd(): void {
    this.pinchRecognizer = undefined
  }

  private getActivePointers(): Phaser.Input.Pointer[] {
    return Array.from(this.activePointers.values())
      .filter((pointer) => pointer.isDown)
      .sort((a, b) => a.id - b.id)
  }

  private clampMapPosition(): void {
    if (!this.mapContainer || this.baseMapScale <= 0) {
      return
    }

    const scale = this.baseMapScale * this.currentZoom
    if (scale <= 0) {
      return
    }

    const mapWidthPx = this.mapCols * (this.maxCellWidthPx || 1)
    const mapHeightPx = this.mapRows * (this.maxCellHeightPx || 1)

    const halfWidth = (mapWidthPx * scale) / 2
    const halfHeight = (mapHeightPx * scale) / 2

    const halfClampWidth = this.clampBounds.width / 2
    const halfClampHeight = this.clampBounds.height / 2

    const xExtent = Math.max(this.clampPadding.x * 0.5, halfWidth - halfClampWidth)
    const yExtent = Math.max(this.clampPadding.y * 0.5, halfHeight - halfClampHeight)

    const minX = this.clampBounds.centerX - xExtent
    const maxX = this.clampBounds.centerX + xExtent
    const minY = this.clampBounds.centerY - yExtent
    const maxY = this.clampBounds.centerY + yExtent

    const clampedX = Phaser.Math.Clamp(this.mapContainer.x, minX, maxX)
    const clampedY = Phaser.Math.Clamp(this.mapContainer.y, minY, maxY)

    this.mapContainer.setPosition(clampedX, clampedY)
  }

  private updateEventMarkers(
    cellWidthPx: number,
    cellHeightPx: number,
    halfWidthPx: number,
    halfHeightPx: number
  ): void {
    const container = this.mapContainer

    if (!container) {
      return
    }

    this.eventMarkers.forEach((marker) => marker.destroy())
    this.eventMarkers = []
    this.eventSprites.forEach((sprite) => sprite.destroy())
    this.eventSprites = []
    Object.values(this.eventObjectsById).forEach((object) => object.destroy())
    this.eventObjectsById = {}
    this.eventHitAreas = {}

    const fontSize = Math.max(12, Math.round(cellHeightPx * 0.28))
    const offsetXFactor = cellWidthPx * 0.28
    const offsetYFactor = cellHeightPx * 0.28

    Object.values(this.campaignEvents).forEach((eventList) => {
      eventList.forEach((eventDef) => {
        const location = this.tileEvents[eventDef.id]

        if (!location) {
          return
        }

        const baseKey = eventDef.id.replace(/[^A-Za-z]/g, '')
        const isTileVisible = this.tileVisibility[baseKey] ?? true

        const tileInstance = this.getTileInstanceAt(location.row, location.col)

        let baseCenterX: number
        let baseCenterY: number
        let offsetBaseX = offsetXFactor
        let offsetBaseY = offsetYFactor

        if (tileInstance) {
          const tileCols = tileInstance.maxCol - tileInstance.minCol + 1
          const tileRows = tileInstance.maxRow - tileInstance.minRow + 1
          const cellWidth = tileCols > 0 ? tileInstance.pixelWidth / tileCols : cellWidthPx
          const cellHeight = tileRows > 0 ? tileInstance.pixelHeight / tileRows : cellHeightPx
          const tileTopLeftX = tileInstance.image.x - tileInstance.pixelWidth / 2
          const tileTopLeftY = tileInstance.image.y - tileInstance.pixelHeight / 2
          const localColOffset = location.col - tileInstance.minCol
          const localRowOffset = location.row - tileInstance.minRow

          baseCenterX = tileTopLeftX + (localColOffset + 0.5) * cellWidth
          baseCenterY = tileTopLeftY + (localRowOffset + 0.5) * cellHeight
          offsetBaseX = cellWidth * 0.28
          offsetBaseY = cellHeight * 0.28
        } else {
          baseCenterX = -halfWidthPx + (location.col + 0.5) * cellWidthPx
          baseCenterY = -halfHeightPx + (location.row + 0.5) * cellHeightPx
        }

        const { x: offsetX, y: offsetY } = this.getEventPositionOffset(eventDef.position, offsetBaseX, offsetBaseY)
        const centerX = baseCenterX + offsetX
        const centerY = baseCenterY + offsetY

        const tokenKey = eventDef.token
        const textureKey = tokenKey ? getCampaignEventTokenTextureKey(ACTIVE_CAMPAIGN_ID, tokenKey) : undefined

        let bounds: Phaser.Geom.Rectangle | undefined

        if (tokenKey && textureKey && this.textures.exists(textureKey)) {
          const sprite = this.add.image(centerX, centerY, textureKey)
          sprite.setOrigin(0.5, 0.5)
          sprite.setDepth(4)
          sprite.setScale(Math.min(1.2, (cellHeightPx * 0.86) / (sprite.height || cellHeightPx)))
          sprite.setVisible(isTileVisible)
          sprite.setInteractive({ useHandCursor: true, pixelPerfect: true })
          container.add(sprite)
          bounds = sprite.getBounds()
          this.eventSprites.push(sprite)
          this.eventObjectsById[eventDef.id] = sprite
        } else {
          const marker = this.add.text(centerX, centerY, eventDef.token || eventDef.id, {
            fontFamily: 'Segoe UI, system-ui, sans-serif',
            fontSize: `${fontSize}px`,
            color: '#fef3c7',
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            padding: { x: 6, y: 2 },
          })
          marker.setOrigin(0.5, 0.5)
          marker.setDepth(4)
          marker.setVisible(isTileVisible)
          marker.setInteractive({ useHandCursor: true })
          container.add(marker)
          bounds = marker.getBounds()
          this.eventMarkers.push(marker)
          this.eventObjectsById[eventDef.id] = marker
        }

        const hitBounds = bounds ?? new Phaser.Geom.Rectangle(
          centerX - cellWidthPx / 2,
          centerY - cellHeightPx / 2,
          cellWidthPx,
          cellHeightPx
        )

        const halfWidth = Math.max(10, hitBounds.width / 2)
        const halfHeight = Math.max(10, hitBounds.height / 2)

        this.eventHitAreas[eventDef.id] = {
          x: hitBounds.centerX,
          y: hitBounds.centerY,
          halfWidth,
          halfHeight,
        }

      })
    })
  }

  private centerMapOnTile(tileId: number, targetWorldX: number, targetWorldY: number): void {
    if (!this.mapContainer) {
      return
    }

    const localPosition = this.tileCentersById[tileId]
    if (!localPosition) {
      return
    }

    const scale = this.baseMapScale * this.currentZoom
    if (scale <= 0) {
      return
    }

    const containerX = targetWorldX - localPosition.x * scale
    const containerY = targetWorldY - localPosition.y * scale

    this.mapContainer.setPosition(containerX, containerY)
  }

  private resolveCampaignText(key: string): string {
    const fallbacks = [this.campaignLocale, 'es', 'en'].filter(Boolean)

    for (const locale of fallbacks) {
      const normalized = locale!.toLowerCase()
      const translation = getCampaignTranslation(ACTIVE_CAMPAIGN_ID, normalized, key)

      if (typeof translation === 'string' && translation.trim().length > 0) {
        return translation
      }
    }

    return key
  }

  private stopModalAudio(): void {
    if (!this.activeModalSoundKey) {
      return
    }

    if (this.sound.get(this.activeModalSoundKey)) {
      this.sound.stopByKey(this.activeModalSoundKey)
    }

    this.activeModalSoundKey = undefined
  }

  private getEventPositionOffset(
    position: string | undefined,
    offsetX: number,
    offsetY: number
  ): { x: number; y: number } {
    switch (position?.toLowerCase()) {
      case 'top':
        return { x: 0, y: -offsetY }
      case 'bottom':
      case 'bottomo':
        return { x: 0, y: offsetY }
      case 'left':
        return { x: -offsetX, y: 0 }
      case 'right':
        return { x: offsetX, y: 0 }
      case 'middle':
      case 'center':
        return { x: 0, y: 0 }
      default:
        return { x: 0, y: 0 }
    }
  }
}
